import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, serif, fmt, Card, Chip, Boton, Titulo, Aviso, Cargando,
} from '../lib/ui';
import {
  BarrasHorizontales, Pareto, Tendencia, Reparto, Cifra,
} from '../lib/graficos';
import { mensajeDeError } from './Gastos';
import { VistaDesviaciones, VistaPresupuesto } from './Desviaciones';

// ════════════════════════════════════════════════════════════════════
// Reportes
//
// Cada persona ve segun su alcance: un operativo sus propios gastos,
// un mando medio los de su equipo, contabilidad y direccion todo.
// El filtro lo aplica el servidor, no esta pantalla.
// ════════════════════════════════════════════════════════════════════

const HOY = new Date();
const primerDiaDe = (mesesAtras = 0) =>
  new Date(HOY.getFullYear(), HOY.getMonth() - mesesAtras, 1).toISOString().slice(0, 10);
const hoyStr = () => HOY.toISOString().slice(0, 10);

const PESTANAS = [
  { id: 'resumen',      texto: 'Resumen' },
  { id: 'desviaciones', texto: 'Desviaciones' },
  { id: 'presupuesto',  texto: 'Presupuesto' },
  { id: 'pareto',       texto: 'Conceptos' },
  { id: 'personas',     texto: 'Personas' },
  { id: 'ovs',          texto: 'Proyectos' },
];

// Estas dos pestañas traen sus propios datos y no usan el cargador comun.
/**
 * Devuelve siempre una lista.
 *
 * "personas" significa una cosa en el resumen (cuántas rindieron, un
 * número) y otra en su propio reporte (la lista). Si una vista recibe
 * el dato de la otra, con esto pinta vacío en vez de tumbar la
 * pantalla entera.
 */
const lista = (v) => (Array.isArray(v) ? v : []);

const AUTONOMAS = ['desviaciones', 'presupuesto'];

export function Reportes({ usuario }) {
  const [pestana, setPestana] = useState('resumen');
  const [filtros, setFiltros] = useState({
    desde: primerDiaDe(0), hasta: hoyStr(), moneda: 'PEN', ov: '',
    // Al empezar casi nada está aprobado todavía. Sin esto los
    // reportes salen vacíos y parecen rotos.
    pendientes: '1',
  });
  const [datos, setDatos] = useState(null);
  // A qué pestaña pertenecen los datos que hay cargados.
  //
  // React pinta ANTES de que corra la carga. Al cambiar de pestaña,
  // la vista nueva se dibujaba una vez con los datos de la anterior:
  // el resumen trae "personas" como número (cuántas rindieron) y la
  // vista de personas espera una lista. Eso reventaba la pantalla.
  const [datosDe, setDatosDe] = useState(null);
  const [tendencia, setTendencia] = useState(null);
  const [ovs, setOvs] = useState([]);
  const [fallo, setFallo] = useState('');

  useEffect(() => {
    api.ovs().then((r) => setOvs(r.ovs || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (AUTONOMAS.includes(pestana)) {
      setDatos({}); setDatosDe(pestana); setFallo('');
      return;
    }
    setDatos(null);
    setDatosDe(null);
    setFallo('');
    const f = { ...filtros };
    if (!f.ov) delete f.ov;
    if (!f.pendientes) delete f.pendientes;

    const consulta = {
      resumen:  () => api.reporteResumen(f),
      pareto:   () => api.reportePareto(f),
      personas: () => api.reportePorPersona(f),
      ovs:      () => api.reportePorOv(f),
    }[pestana];

    // La pestaña se guarda con los datos: si el usuario cambia de
    // pestaña mientras la consulta viaja, la respuesta que llega tarde
    // no se pinta en la vista equivocada.
    const pedida = pestana;
    consulta()
      .then((r) => { setDatos(r); setDatosDe(pedida); })
      .catch((err) => {
        setFallo(mensajeDeError(err));
        setDatos({});
        setDatosDe(pedida);
      });
  }, [pestana, filtros]);

  useEffect(() => {
    if (pestana !== 'resumen') return;
    api.reporteTendencia({ meses: 6, moneda: filtros.moneda })
      .then(setTendencia)
      .catch(() => setTendencia(null));
  }, [pestana, filtros.moneda]);

  const set = (k, v) => setFiltros((x) => ({ ...x, [k]: v }));

  const descargar = () => {
    // La descarga necesita el token, asi que se pide y se guarda como
    // archivo en lugar de abrir una pestaña nueva.
    api.exportarGastos(filtros)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gastos-${filtros.desde}-a-${filtros.hasta}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setFallo('No se pudo generar el archivo.'));
  };

  return (
    <>
      <Titulo sub="Todo el periodo elegido, según lo que te corresponde ver">
        Reportes
      </Titulo>

      {/* Filtros */}
      <Card style={{ marginBottom: 14, padding: 14 }}>
        <div style={{
          display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        }}>
          <label style={{ fontSize: 11, color: T.muted }}>
            Desde
            <input type="date" value={filtros.desde}
                   onChange={(e) => set('desde', e.target.value)}
                   style={campoFiltro} />
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            Hasta
            <input type="date" value={filtros.hasta}
                   onChange={(e) => set('hasta', e.target.value)}
                   style={campoFiltro} />
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            Moneda
            <select value={filtros.moneda} onChange={(e) => set('moneda', e.target.value)}
                    style={campoFiltro}>
              <option value="PEN">Soles</option>
              <option value="USD">Dólares</option>
            </select>
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            Proyecto
            <select value={filtros.ov} onChange={(e) => set('ov', e.target.value)}
                    style={campoFiltro}>
              <option value="">Todos</option>
              {ovs.map((o) => <option key={o.id} value={o.id}>{o.id}</option>)}
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
          {[['Este mes', 0], ['Mes pasado', 1], ['3 meses', 3], ['Año', 11]].map(([txt, n]) => (
            <button key={txt}
                    onClick={() => setFiltros((x) => ({
                      ...x,
                      desde: primerDiaDe(n),
                      hasta: n === 1
                        ? new Date(HOY.getFullYear(), HOY.getMonth(), 0).toISOString().slice(0, 10)
                        : hoyStr(),
                    }))}
                    style={{
                      padding: '5px 11px', borderRadius: 14, fontSize: 12,
                      border: `1px solid ${T.border}`, background: T.paper,
                      color: T.text, cursor: 'pointer',
                    }}>
              {txt}
            </button>
          ))}
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          fontSize: 12, marginTop: 12, cursor: 'pointer',
          padding: '9px 11px', borderRadius: 8,
          background: filtros.pendientes === '1' ? T.warnBg : T.warm,
        }}>
          <input
            type="checkbox"
            checked={filtros.pendientes === '1'}
            onChange={(e) => set('pendientes', e.target.checked ? '1' : '')}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Incluir los que están por aprobar</strong>
            <span style={{ display: 'block', color: T.muted, marginTop: 2 }}>
              {filtros.pendientes === '1'
                ? 'Se cuentan aprobados, contabilizados, enviados y observados. '
                  + 'Sirve para ver el mes en curso; las cifras pueden cambiar al aprobar.'
                : 'Solo aprobados y contabilizados. Es la cifra firme, pero deja fuera '
                  + 'lo que todavía no se decide.'}
            </span>
          </span>
        </label>
      </Card>

      {/* Pestañas */}
      <div style={{ display: 'flex', gap: 7, marginBottom: 14, flexWrap: 'wrap' }}>
        {PESTANAS.map((p) => (
          <button key={p.id} onClick={() => setPestana(p.id)} style={{
            padding: '7px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
            border: `1px solid ${pestana === p.id ? T.navy : T.border}`,
            background: pestana === p.id ? T.navy : T.paper,
            color: pestana === p.id ? '#fff' : T.text,
          }}>
            {p.texto}
          </button>
        ))}
      </div>

      {fallo && <Aviso tipo="bad">{fallo}</Aviso>}
      {/* Solo se pinta cuando los datos son de ESTA pestaña. */}
      {!datos || datosDe !== pestana ? <Cargando /> : (
        <>
          {pestana === 'resumen'      && <VistaResumen d={datos} tendencia={tendencia} moneda={filtros.moneda} />}
          {pestana === 'desviaciones' && <VistaDesviaciones filtros={filtros} moneda={filtros.moneda} />}
          {pestana === 'presupuesto'  && <VistaPresupuesto filtros={filtros} moneda={filtros.moneda} usuario={usuario} />}
          {pestana === 'pareto'   && <VistaPareto d={datos} moneda={filtros.moneda} />}
          {pestana === 'personas' && <VistaPersonas d={datos} moneda={filtros.moneda} />}
          {pestana === 'ovs'      && <VistaOvs d={datos} moneda={filtros.moneda} filtros={filtros} />}

          {!AUTONOMAS.includes(pestana) && (
            <Boton variante="suave" onClick={descargar}
                   style={{ width: '100%', marginTop: 18 }}>
              Descargar detalle en Excel
            </Boton>
          )}
        </>
      )}
    </>
  );
}

const campoFiltro = {
  width: '100%', padding: '7px 9px', fontSize: 13, marginTop: 3,
  border: `1px solid ${T.border}`, borderRadius: 7,
  background: T.paper, color: T.text, boxSizing: 'border-box',
};

// ════════════════════════════════════════════════════════════════════
function VistaResumen({ d, tendencia, moneda }) {
  if (!d.total && !d.cantidad) {
    return <Aviso tipo="info">No hay gastos en el periodo elegido.</Aviso>;
  }

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <div style={{
          display: 'grid', gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
        }}>
          <Cifra etiqueta="Gasto total" valor={fmt.money(d.total, moneda)}
                 detalle={`${d.cantidad} rendición(es)`} />
          <Cifra etiqueta="Promedio" valor={fmt.money(d.promedio, moneda)}
                 detalle="por gasto" />
          <Cifra etiqueta="Personas" valor={d.personas} detalle="rindieron" />
          <Cifra etiqueta="Proyectos" valor={d.proyectos} detalle="con gasto" />
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <TituloBloque>Con proyecto y sin proyecto</TituloBloque>
        <Reparto
          moneda={moneda}
          partes={[
            { etiqueta: 'Imputado a una OV', valor: d.imputadoAProyecto, color: T.navy },
            { etiqueta: 'Sin proyecto', valor: d.sinProyecto, color: T.brass },
          ]}
        />
        {d.sinProyecto > d.imputadoAProyecto && (
          <div style={{ fontSize: 12, color: T.warn, marginTop: 11 }}>
            Más de la mitad del gasto no está imputado a ningún proyecto. Vale la
            pena revisar si falta asignar la OV al capturar.
          </div>
        )}
      </Card>

      {tendencia && (
        <Card style={{ marginBottom: 14 }}>
          <TituloBloque>Últimos 6 meses</TituloBloque>
          <Tendencia serie={tendencia.serie} moneda={moneda} />
        </Card>
      )}

      <Card>
        <TituloBloque>Cómo se sustenta</TituloBloque>
        <BarrasHorizontales
          moneda={moneda}
          datos={lista(d.porTipo).map((t) => ({
            clave: t.tipo,
            etiqueta: {
              comprobante: 'Factura o boleta',
              planilla_movilidad: 'Planilla de movilidad',
              recibo_ad: 'Recibo interno',
              sin_comprobante: 'Sin comprobante',
            }[t.tipo] || t.tipo,
            detalle: `${t.cantidad}`,
            valor: t.total,
          }))}
        />
      </Card>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function VistaPareto({ d, moneda }) {
  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <TituloBloque>En qué se gasta</TituloBloque>
        <div style={{ fontSize: 13, color: T.text, marginBottom: 14 }}>
          {d.lectura}
        </div>
        <Pareto conceptos={d.conceptos} vitales={d.vitales} moneda={moneda} />
      </Card>

      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
        Las barras oscuras son los conceptos que acumulan el 80% del gasto. Si
        quieres reducir costos, ahí está el margen: los demás, sumados, mueven
        poco la aguja.
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function VistaPersonas({ d, moneda }) {
  const conObservados = lista(d.personas).filter((p) => p.observados > 0);

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <TituloBloque>Gasto por persona</TituloBloque>
        <BarrasHorizontales
          moneda={moneda}
          maxFilas={20}
          datos={lista(d.personas).map((p) => ({
            clave: p.usuarioId,
            etiqueta: p.nombre || 'Sin nombre',
            detalle: `${p.cantidad} gasto(s) · ${p.centro || ''}`,
            valor: p.total,
            participacion: p.participacion,
          }))}
        />
      </Card>

      {conObservados.length > 0 && (
        <Card>
          <TituloBloque>Con gastos observados</TituloBloque>
          <div style={{ display: 'grid', gap: 8 }}>
            {conObservados.map((p) => (
              <div key={p.usuarioId} style={{
                display: 'flex', justifyContent: 'space-between', fontSize: 13,
              }}>
                <span>{p.nombre}</span>
                <span style={{ color: T.warn, fontWeight: 600 }}>
                  {p.observados} por corregir
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function VistaOvs({ d, moneda, filtros }) {
  const proyectos = lista(d.proyectos);
  // Desglose del proyecto que se tocó. El reporte daba el total y ahí
  // se acababa: para saber en qué se fue había que salir a buscarlo
  // gasto por gasto.
  const [abierto, setAbierto] = useState(null);
  const consumoAlto = proyectos.filter((p) => p.consumoContrato != null && p.consumoContrato > 15);

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <TituloBloque>Gasto por proyecto</TituloBloque>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 11 }}>
          Toca un proyecto para ver en qué se fue.
        </div>
        <BarrasHorizontales
          moneda={moneda}
          maxFilas={20}
          alTocar={(x) => setAbierto(x.clave === 'sin' ? 'sin-ov' : x.clave)}
          datos={proyectos.map((p) => ({
            clave: p.ovId || 'sin',
            etiqueta: p.ovId ? `${p.ovId} · ${p.cliente}` : p.cliente,
            detalle: `${p.cantidad} gasto(s)`,
            valor: p.total,
            participacion: p.participacion,
            color: p.ovId ? T.navy : T.brass,
          }))}
        />
      </Card>

      {abierto && (
        <DesgloseProyecto
          ovId={abierto}
          filtros={filtros}
          moneda={moneda}
          onCerrar={() => setAbierto(null)}
        />
      )}

      {consumoAlto.length > 0 && (
        <Card>
          <TituloBloque>Consumo sobre el contrato</TituloBloque>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 11 }}>
            Cuánto de cada contrato se ha ido en gastos rendidos. Solo aparece
            cuando la moneda del contrato coincide con la del filtro.
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {consumoAlto.map((p) => (
              <div key={p.ovId}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4,
                }}>
                  <span>{p.ovId} · {p.cliente}</span>
                  <span style={{
                    fontWeight: 600,
                    color: p.consumoContrato > 40 ? T.bad : T.navy,
                  }}>
                    {p.consumoContrato.toFixed(1)}%
                  </span>
                </div>
                <div style={{ height: 7, background: T.warm, borderRadius: 4 }}>
                  <div style={{
                    width: `${Math.min(p.consumoContrato, 100)}%`, height: '100%',
                    background: p.consumoContrato > 40 ? T.bad : T.navy, borderRadius: 4,
                  }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

function TituloBloque({ children }) {
  return (
    <div style={{
      fontSize: 11, color: T.muted, fontWeight: 600,
      letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 12,
    }}>
      {children}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Desglose de un proyecto
//
// Dos vistas, que son las dos preguntas que uno se hace al tocar un
// proyecto: en qué TIPO de cosa se fue el dinero, y cuáles son los
// gastos concretos para poder llegar al documento.
// ════════════════════════════════════════════════════════════════════
function DesgloseProyecto({ ovId, filtros, moneda, onCerrar }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setD(null); setError('');
    api.reporteProyecto(ovId, filtros)
      .then(setD)
      .catch((e) => setError(e.detalle?.detalle || 'No se pudo cargar el desglose.'));
  }, [ovId, JSON.stringify(filtros)]);   // eslint-disable-line

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(10,15,26,.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.cream, borderRadius: '14px 14px 0 0',
          width: '100%', maxWidth: 860, maxHeight: '88vh', overflowY: 'auto',
          padding: 18,
          paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 0px))',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12, marginBottom: 14,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: serif, fontSize: 19, fontWeight: 600, color: T.navy }}>
              {d?.ov ? `${d.ovId} · ${d.ov.clienteAlias}` : (d?.ovId || 'Gastos sin proyecto')}
            </div>
            {d?.ov?.descripcion && (
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                {d.ov.descripcion}
              </div>
            )}
          </div>
          <Boton variante="suave" onClick={onCerrar} style={{ fontSize: 13, padding: '6px 13px' }}>
            Cerrar
          </Boton>
        </div>

        {error && <Aviso tipo="bad">{error}</Aviso>}
        {!d && !error && <Cargando />}

        {d && (
          <>
            <Card style={{ marginBottom: 14 }}>
              <TituloBloque>En qué se fue</TituloBloque>
              <BarrasHorizontales
                moneda={moneda}
                maxFilas={15}
                datos={lista(d.cuentas).map((c) => ({
                  clave: c.cuenta,
                  etiqueta: `${c.cuenta} · ${c.nombre}`,
                  detalle: `${c.cantidad} gasto(s)`,
                  valor: c.total,
                  participacion: c.participacion,
                  color: T.navy,
                }))}
              />
            </Card>

            <Card>
              <TituloBloque>
                Cada gasto ({lista(d.gastos).length})
              </TituloBloque>
              {d.recortado && (
                <Aviso tipo="warn">
                  Se muestran los 500 más recientes. Para el total completo,
                  usa la exportación.
                </Aviso>
              )}
              <div style={{ display: 'grid', gap: 7 }}>
                {lista(d.gastos).map((g) => (
                  <div key={g.id} style={{
                    display: 'flex', justifyContent: 'space-between',
                    gap: 11, padding: '9px 0',
                    borderBottom: `1px solid ${T.border}`,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {g.concepto || g.proveedor || 'Sin concepto'}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                        {fmt.fecha(g.fecha)}
                        {g.comprobante ? ` · ${g.comprobante}` : ''}
                        {` · ${g.cuenta} ${g.nombreCuenta}`}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>
                        {g.autor || 'sin identificar'}
                        {/* Quien rindió no siempre es quien gastó. */}
                        {g.consumidor && g.consumidor !== g.autor
                          ? ` · lo gastó ${g.consumidor}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {fmt.money(g.total, g.moneda)}
                      </div>
                      <Chip estado={g.estado} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
